import CreditConditionalButton from "@/components/core/buttons/CreditConditionalButton";
import { requestGetTransactionDetail } from "@/state/transactions/TransactionEffects";
import { ActiveUser } from "@/type/core/auth/auth.types";
import { Signatory } from "@/type/core/dashboard/dashboard.types";
import { Right } from "@/type/core/offices/offices.types";
import { TransactionDetail } from "@/type/core/transaction/transaction.types";
import { handleRelaunchSignatory } from "@/utils/helpers/signatories-helper";
import BasicButton from "@components/core/buttons/BasicButton";
import InfoModal from "@components/core/modals/InfoModal";
import CheckIcon from "@static/core/icons/icon-check.svg?react";
import CrossIcon from "@static/core/icons/icon-cross.svg?react";
import { dateToMessage } from "@views/core/extranet/transactions/details/sections/TransactionTitleSection";
import i18n from "i18next";
import { Dispatch, FunctionComponent, SetStateAction } from "react";
import { useTranslation } from "react-i18next";

interface RelaunchDetailSignatoryComponentProps {
  keyId: string;
  signatory: Signatory;
  transaction: TransactionDetail;
  authorized: boolean;
  editMode?: string;
  setEditMode: (editMode: string) => void;
  updateSignatory: () => void;
  setTemporaryEditableSignatory?: Dispatch<
    SetStateAction<Signatory | undefined>
  >;
  directSignature?: boolean;
  isOrderChanged: boolean;
  signatureDegradedMode: boolean;
  isOtherSignatoryInProgress?: boolean;
  setIsLoading: (isLoading: boolean) => void;
  user?: ActiveUser;
  officeRights: Right[];
}

const RelaunchDetailSignatoryComponent: FunctionComponent<
  RelaunchDetailSignatoryComponentProps
> = ({
  keyId,
  signatory,
  transaction,
  authorized,
  editMode,
  setEditMode,
  updateSignatory,
  setTemporaryEditableSignatory,
  directSignature,
  isOrderChanged,
  signatureDegradedMode,
  isOtherSignatoryInProgress,
  setIsLoading,
  user,
  officeRights,
}) => {
  keyId = keyId ?? signatory.id;

  const { t } = useTranslation();

  const signed = signatory.status === "signed";
  const refused = signatory.status === "refused";
  const currentSignatory =
    (signatory.status === "waiting" ||
      signatory.status === "inProgress" ||
      signatory.status === "error" ||
      signatory.status === "simultaneous") &&
    transaction &&
    transaction.status === "inProgress" &&
    authorized;
  const inProgressDate = signatory.inProgressDate;
  const invitationDate = signatory.invitationDate;
  const relaunchDate = signatory.relaunchDate;
  let currentSignatoryDate = null;
  let inParallelProgress = false;
  const noLongerRequired = signatory.status === "no_longer_required";
  if (transaction.invitationMode === "parallel") {
    if (
      signatory.email === user?.email &&
      !!user?.certificateInfos &&
      !!user?.certificateInfos.serialNumber &&
      officeRights.includes("SIGNATURE_COLLECTE_SIGNER")
    ) {
      directSignature = true;
    } else {
      directSignature = false;
    }
  }

  if (
    signatory.status === "inProgress" &&
    transaction.invitationMode === "parallel" &&
    inProgressDate
  ) {
    inParallelProgress = true;
    currentSignatoryDate = i18n.t("transaction.detail.signatory.inProgress", {
      date: dateToMessage(inProgressDate),
    });
  } else if (currentSignatory && invitationDate && !relaunchDate) {
    if (editMode) {
      currentSignatoryDate = i18n.t(
        "transaction.detail.signatory.invitedEdited",
        {
          date: dateToMessage(invitationDate),
        },
      );
    } else {
      currentSignatoryDate = i18n.t("transaction.detail.signatory.invited", {
        date: dateToMessage(invitationDate),
      });
    }
  } else if (
    (currentSignatory && !invitationDate && relaunchDate) ||
    (currentSignatory && invitationDate && relaunchDate)
  ) {
    currentSignatoryDate = i18n.t("transaction.detail.signatory.relaunched", {
      date: dateToMessage(relaunchDate),
    });
  }
  const relaunch = currentSignatory && editMode !== keyId;

  const getSignatureDateMessage = (
    signed: boolean,
    refused: boolean,
    date: string,
  ) => {
    return (
      (signed ? "Signé" : refused ? "Refus" : "") +
      (signed || refused ? " le " + dateToMessage(date) : "")
    );
  };

  return editMode === keyId ? (
    <div className="d-flex align-items-center justify-content-end gap-8">
      <div className="d-flex align-items-center text-danger">
        <InfoModal value="signatoryUpdated" warning info={false} />
        {i18n.t("transaction.detail.signatory.updateConfirmation")}
      </div>
      <BasicButton
        text={i18n.t("transaction.detail.validateButton")}
        onClick={() => {
          updateSignatory();
          setEditMode("");
        }}
      />
      <BasicButton
        variant="secondary"
        text={i18n.t("transaction.detail.cancelButton")}
        onClick={() => {
          setTemporaryEditableSignatory?.(undefined);
          setEditMode("");
        }}
      />
    </div>
  ) : (
    <div
      title={getSignatureDateMessage(
        signed,
        refused,
        signatory.signatureDate as string,
      )}
      className="d-flex align-items-center justify-content-end gap-16"
    >
      {signed && <CheckIcon className="text-success icon-30" />}
      {refused && <CrossIcon className="text-danger icon-30" />}
      {(signed || refused) &&
        getSignatureDateMessage(
          signed,
          refused,
          signatory.signatureDate as string,
        )}
      {noLongerRequired && (
        <span className="text-muted">
          {i18n.t("transaction.detail.signatory.noLongerRequired")}
        </span>
      )}
      {relaunch && (
        <div
          className={`d-flex align-items-center gap-8 ${isOrderChanged ? "invisible" : ""}`}
        >
          {currentSignatoryDate && (
            <div
              className={
                inParallelProgress ? "text-info in-progress-since" : ""
              }
            >
              {currentSignatoryDate}
            </div>
          )}
          {!directSignature ? (
            transaction.notification != "DISABLED" && (
              <CreditConditionalButton
                isRelaunch
                transaction={transaction}
                disabled={signatureDegradedMode}
                onClick={() =>
                  void handleRelaunchSignatory({
                    signatureDegradedMode,
                    signatoryEmail: signatory.email,
                    signatoryId: signatory.id,
                    transactionId: transaction.id,
                    transactionInvitationMode: transaction.invitationMode,
                    setIsLoading,
                    callbackOnSuccess: () =>
                      void requestGetTransactionDetail({
                        id: transaction.id,
                      }),
                  })
                }
                className={"btn btn-primary"}
                label={t("transaction.detail.signatoryRelaunch")}
              />
            )
          ) : (
            <CreditConditionalButton
              transaction={transaction}
              disabled={isOtherSignatoryInProgress || signatureDegradedMode}
              isDirectSignature
              onClick={() => {
                if (!isOtherSignatoryInProgress && !signatureDegradedMode) {
                  window.open(signatory.url);
                }
              }}
              className={"btn btn-primary"}
              label={t("transaction.detail.sign")}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default RelaunchDetailSignatoryComponent;
